import { Navigate } from "react-router";
import { Spinner } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useStaffContext } from "../shell/useStaffContext";
import { recallLastVenue } from "../shell/lastVenue";

/**
 * Root `/` resolver: send the user to their last-used venue (sessionStorage hint) when it is still
 * assigned, else the first assigned venue, else the picker. Keeps the entry URL clean while landing
 * everyone somewhere useful.
 */
export function RootRedirect() {
  const { venues, isLoading, claims } = useStaffContext();
  const { t } = useTranslation();

  // Return leg of the "Connect Google Business" OAuth flow: the backend callback lands here on `/`
  // (the configured app return URL) with `?google_business=<result>`. Before redirecting to Google
  // the card stashed the originating path (the Channels→Google tab) in sessionStorage; restore it
  // now, carrying the result param, so the card shows feedback + auto-syncs instead of the param
  // being lost in the venue redirect below.
  const gbResult = new URLSearchParams(window.location.search).get("google_business");
  const gbReturn = gbResult ? sessionStorage.getItem("gb_return") : null;
  if (gbResult && gbReturn) {
    sessionStorage.removeItem("gb_return");
    const sep = gbReturn.includes("?") ? "&" : "?";
    return (
      <Navigate to={`${gbReturn}${sep}google_business=${encodeURIComponent(gbResult)}`} replace />
    );
  }

  // Signed in but never provisioned -> finish signup. This is the RETURN LEG of the Google flow:
  // the OIDC redirect_uri is the app origin (VITE_OIDC_REDIRECT_URI, defaulting to location.origin),
  // so an owner coming back from Google lands on `/`, not on `/signup`. Deciding it here rather
  // than juggling redirect URIs means the flow resumes wherever the issuer chooses to drop them.
  //
  // Checked BEFORE the venue query is consulted: a tenant-less token has no venues to fetch, so the
  // alternative is a pointless round trip ending at an empty picker that offers no way forward.
  if (!claims.tenantId) {
    return <Navigate to="/signup" replace />;
  }

  if (isLoading) {
    return (
      <div className={css({ display: "flex", justifyContent: "center", p: "16" })}>
        <Spinner aria-label={t("shell.loading")} />
      </div>
    );
  }

  const last = recallLastVenue();
  const target = venues.find((v) => v.id === last) ?? venues[0];
  return <Navigate to={target ? `/v/${target.id}` : "/select-venue"} replace />;
}
